/** 選んだ画面の機能を表で示す（中央）。行を選ぶと右に詳細が開く */

import type { AiUsageGroup, AiUsageItem } from '@/api/types/ai-usages'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

import { KIND_LABELS, quotaRatio, quotaTone } from './ai-usage-utils'

const TONE_BAR: Record<ReturnType<typeof quotaTone>, string> = {
  none: '',
  ok: '',
  warn: '[&>*]:bg-amber-500',
  danger: '[&>*]:bg-destructive',
}

const ModelChips = ({ item }: { item: AiUsageItem }) => {
  const [first, ...rest] = item.models
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Badge variant="secondary" className="font-mono">
        {first?.name}
      </Badge>
      {rest.length > 0 && (
        <span className="text-xs text-muted-foreground" title={rest.map((model) => model.name).join(' → ')}>
          +{rest.length}
        </span>
      )}
    </div>
  )
}

const QuotaCell = ({ item }: { item: AiUsageItem }) => {
  const first = item.models.at(0)
  const ratio = quotaRatio(first)
  if (first && first.quota_source === 'unknown' && first.used_today !== null) {
    return (
      <span className="text-xs text-muted-foreground" title="上限に当たったら、その時点の回数を覚えます">
        上限未確認（今日 {first.used_today} 回）
      </span>
    )
  }
  if (ratio === null || !first) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <div className="w-28 space-y-1" title={`${first.name} の今日の無料枠`}>
      <Progress value={ratio * 100} className={cn('h-1.5', TONE_BAR[quotaTone(ratio)])} />
      <p className="text-xs tabular-nums text-muted-foreground">
        {first.used_today} / {first.daily_limit}
      </p>
    </div>
  )
}

const PaidCell = ({ item }: { item: AiUsageItem }) => {
  if (!item.allow_paid) return <span className="text-xs text-muted-foreground">切り替えない</span>
  return (
    <div className="text-xs tabular-nums" title="有料のキーで答えが返った回数">
      <span
        className={item.paid_today > 0 ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}
      >
        今日 {item.paid_today} 回
      </span>
      <span className="ml-2 text-muted-foreground">7日 {item.paid_last_7_days}</span>
    </div>
  )
}

const UsageRow = ({ item, selected, onSelect }: { item: AiUsageItem; selected: boolean; onSelect: () => void }) => (
  <TableRow
    data-state={selected ? 'selected' : undefined}
    className="cursor-pointer data-[state=selected]:bg-primary/15"
    onClick={onSelect}
  >
    <TableCell className="min-w-52 max-w-80 whitespace-normal">
      <button
        type="button"
        aria-current={selected ? 'true' : undefined}
        className="text-left font-medium outline-none hover:underline focus-visible:underline"
        onClick={(e) => {
          e.stopPropagation()
          onSelect()
        }}
      >
        {item.label}
      </button>
      {item.customized && (
        <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-600 dark:text-amber-400">
          変更済み
        </Badge>
      )}
      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
        <span className="mr-1 rounded bg-muted px-1 py-0.5 text-[10px]">{KIND_LABELS[item.kind]}</span>
        {item.description}
      </p>
    </TableCell>
    <TableCell>
      <ModelChips item={item} />
    </TableCell>
    <TableCell>
      <QuotaCell item={item} />
    </TableCell>
    <TableCell>
      <PaidCell item={item} />
    </TableCell>
  </TableRow>
)

type Props = {
  items: AiUsageItem[]
  /** 「すべて」のとき見出しに使う */
  groups: AiUsageGroup[]
  showGroupHeadings: boolean
  selectedKey: string | null
  onSelect: (key: string) => void
}

export const AiUsageTable = ({ items, groups, showGroupHeadings, selectedKey, onSelect }: Props) => {
  if (items.length === 0) {
    return <p className="p-8 text-center text-sm text-muted-foreground">条件に合う機能はありません</p>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>機能</TableHead>
          <TableHead>最初に試すモデル</TableHead>
          <TableHead>今日の無料枠</TableHead>
          <TableHead>有料のキー</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.map((group) => {
          const rows = items.filter((item) => item.group === group.key)
          if (rows.length === 0) return null
          return [
            showGroupHeadings && (
              <TableRow key={`heading-${group.key}`} className="bg-muted/40 hover:bg-muted/40">
                <TableCell colSpan={4} className="py-1.5 text-xs font-medium text-muted-foreground">
                  {group.label}
                </TableCell>
              </TableRow>
            ),
            ...rows.map((item) => (
              <UsageRow
                key={item.key}
                item={item}
                selected={item.key === selectedKey}
                onSelect={() => onSelect(item.key)}
              />
            )),
          ]
        })}
      </TableBody>
    </Table>
  )
}
